import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import BoardPagination from "../../components/common/board/BoardPagination";
import { getCosts } from "../../api/contract/cost";
import Cost_new from "./Cost_new";
import Cost_view from "./Cost_view";
import NivoBar from "../../components/common/graphs/NivoBar";
import PageHeader from "../../components/common/ui/PageHeader";
import { formatDate } from "../../utils/date";

const Cost = () => {
  const { id: projectId } = useParams();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  const [plannedCost, setPlannedCost] = useState(0);
  const [usedCost, setUsedCost] = useState(0);
  const [totalCost, setTotalCost] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [constructionType, setConstructionType] = useState(null);

  // 그래프 모드
  const [activeChart, setActiveChart] = useState("monthly");

  const monthlyData = useMemo(() => buildMonthlyData(data), [data]);
  const cumulativeData = useMemo(
    () => buildCumulativeData(monthlyData),
    [monthlyData]
  );
  const currentYear = new Date().getFullYear();

  // 비용 데이터 가져오기
  const fetchData = async () => {
    setLoading(true);
    setErrMsg("");

    try {
      const res = await getCosts(projectId, constructionType, page, pageSize);
      setData(res.results || []);
      setTotalCost(res.total_cost ?? 0);
      setPlannedCost(res.planned_cost ?? 0);
      setUsedCost(res.used_cost ?? 0);
      setTotal(res.count || 0);
    } catch (err) {
      console.error(err);
      setErrMsg("목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId, page, pageSize]);

  // 월별 데이터 구성
  function buildMonthlyData(rawData, year = new Date().getFullYear()) {
    const grouped = {};

    rawData.forEach((row) => {
      if (!row.meta?.payment_date) return;

      const date = new Date(row.meta.payment_date);
      const month = date.getMonth() + 1;
      const y = date.getFullYear();

      if (y !== year) return;

      if (!grouped[month]) {
        grouped[month] = { id: `${month}월`, 계획: 0, 실제: 0 };
      }

      grouped[month].계획 += Number(row.meta.expected_amount || 0);
      grouped[month].실제 += Number(row.meta.actual_amount || 0);
    });

    const fullYear = [];
    for (let m = 1; m <= 12; m++) {
      fullYear.push(grouped[m] || { id: `${m}월`, 계획: 0, 실제: 0 });
    }

    return fullYear;
  }

  function buildCumulativeData(monthlyData) {
    const cumulative = [];
    let sumPlan = 0;
    let sumActual = 0;

    monthlyData.forEach((row) => {
      sumPlan += row.계획;
      sumActual += row.실제;

      cumulative.push({
        id: row.id,
        계획: sumPlan,
        실제: sumActual,
      });
    });

    return cumulative;
  }

  const rows = useMemo(() => data ?? [], [data]);

  const handleRowClick = (costId) => {
    setSelectedId(costId);
  };

  return (
    <div className=" mx-auto p-6">
      <PageHeader>기성 현황</PageHeader>
      <div>
        <div className="flex items-start space-x-8">
          {/* 왼쪽: 총 금액 */}
          {/* <h2 className="text-2xl font-bold whitespace-nowrap">
            총 금액: {Number(totalCost).toLocaleString("ko-KR")}{" "}
            원
          </h2> */}

          {/* 오른쪽: 계획/사용 프로그래스바 */}
          <div className="w-full border bg-white shadow-sm mb-6">
            {/* 제목 */}
            <div className="section-header">기성 요약</div>

            <div className="p-6 flex gap-10 items-start">
              {/* ===== 왼쪽: 총 금액 ===== */}
              <div className="min-w-[180px] mt-2">
                <div className="text-gray-500 text-xs mb-1">총 금액</div>
                <div className="text-2xl font-bold text-gray-900 leading-snug">
                  {Number(totalCost).toLocaleString("ko-KR")} 원
                </div>
              </div>

              {/* ===== 오른쪽: 계획/사용 그래프 ===== */}
              <div className="flex-1 space-y-5">
                {/* --- 계획 금액 --- */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs font-medium text-blue-700">
                      계획 총 금액 {Number(plannedCost).toLocaleString("ko-KR")}{" "}
                      원 ({((plannedCost / totalCost) * 100).toFixed(1)}%)
                    </span>
                    <span className="text-xs text-gray-500">
                      남은 금액{" "}
                      {Number(totalCost - plannedCost).toLocaleString("ko-KR")}{" "}
                      원
                    </span>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{
                        width: `${((plannedCost / totalCost) * 100).toFixed(
                          1
                        )}%`,
                      }}
                    ></div>
                  </div>
                </div>

                {/* --- 사용 금액 --- */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs font-medium text-orange-700">
                      사용 총 금액 {Number(usedCost).toLocaleString("ko-KR")} 원{" "}
                      ({((usedCost / totalCost) * 100).toFixed(1)}%)
                    </span>
                    <span className="text-xs text-gray-500">
                      남은 금액{" "}
                      {Number(totalCost - usedCost).toLocaleString("ko-KR")} 원
                    </span>
                  </div>

                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-orange-500 h-2.5 rounded-full"
                      style={{
                        width: `${((usedCost / totalCost) * 100).toFixed(1)}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 mb-4 flex flex-col gap-4">
        {/* 월별 막대그래프 */}
        <div className="w-full border bg-white shadow-sm pb-9">
          <NivoBar
            title={`${currentYear}년 계획 vs 실제 월별`}
            data={monthlyData}
            keys={["계획", "실제"]}
            indexBy="id"
            height={300}
            groupMode="grouped"
            colors={({ id }) => (id === "계획" ? "#3B82F6" : "#F59E0B")}
            showLine={true}
            lineKeys={["계획", "실제"]}
            lineColors={{ 계획: "#f50808ff", 실제: "#1900ffff" }}
          />
        </div>
        {/* 누적 막대그래프 */}
        <div className="w-full border bg-white shadow-sm pb-9">
          {/* 월별 누적 계획 vs 실제 */}
          <NivoBar
            title={`${currentYear}년 월별 계획 vs 실제 월별 누적`}
            data={cumulativeData}
            keys={["계획", "실제"]}
            indexBy="id"
            height={300}
            groupMode="grouped" // 나란히 보여주고 싶으면 grouped, 한 막대에 합치려면 stacked
            colors={({ id }) => (id === "계획" ? "#3B82F6" : "#F59E0B")}
            showLine={true} // 누적 합계 라인도 같이 그릴 수 있음
            lineKeys={["계획", "실제"]}
            lineColors={{ 계획: "#f50808ff", 실제: "#1900ffff" }}
          />
        </div>
      </div>
    </div>
  );
};

export default Cost;